import React from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/public/Home";
import Ministry from "./pages/public/Ministry";
import News from "./pages/public/News";
import Announcements from "./pages/public/Announcements";
import Legislation from "./pages/public/Legislation";
import Contact from "./pages/public/Contact";
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import Ministers from "./pages/admin/Ministers";
import MinisterForm from "./pages/admin/MinisterForm";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import NewsAdmin from "./pages/admin/News";
import NewsForm from "./pages/admin/NewsForm";
import NewsCategories from "./pages/admin/NewsCategories";
import CategorieForm from "./pages/admin/CategorieForm";
import AnnouncementsAdmin from "./pages/admin/Announcements";
import AnnouncementForm from "./pages/admin/AnnouncementForm";
import LegislationsAdmin from "./pages/admin/Legislations";
import LegislationForm from "./pages/admin/LegislationForm";
import NewsDetail from "./pages/public/NewsDetail";
import InformationAdmin from "./pages/admin/Information";
import MessagesAdmin from "./pages/admin/Messages";
import SliderList from "./pages/admin/SliderList";
import SliderForm from "./pages/admin/SliderForm";
import BibliothequeCategories from "./pages/admin/bibliotheque/BibliothequeCategories";
import BibliothequeCategorieForm from "./pages/admin/bibliotheque/CategorieForm";
import BibliothequeList from "./pages/admin/bibliotheque/BibliothequeList";
import BibliothequeForm from "./pages/admin/bibliotheque/BibliothequeForm";
import MinisterPage from "./pages/public/MinisterPage";
import AnnouncementDetail from "./pages/public/AnnouncementDetail";
import { useEffect } from "react";
import Users from "./pages/admin/Users";
import UserForm from "./pages/admin/UserForm";
import SousEtablissementsList from "./pages/admin/sousetablissements/SousEtablissementsList";
import SousEtablissementForm from "./pages/admin/sousetablissements/SousEtablissementForm";
import CabinetMinistreList from "./pages/admin/cabinetministre/CabinetMinistreList";
import CabinetMinistreForm from "./pages/admin/cabinetministre/CabinetMinistreForm";
import StructureAdminList from "./pages/admin/StructureAdminList";
import StructureAdminForm from "./pages/admin/StructureAdminForm";
import MotMinistreList from "./pages/admin/MotMinistreList";
import MotMinistreForm from "./pages/admin/MotMinistreForm";
import ProjetsList from "./pages/admin/ProjetsList";
import ProjetForm from "./pages/admin/ProjetForm";
import PhotosPage from "./pages/public/Photos";
import VideosPage from "./pages/public/Videos";
import ProjectsPage from "./pages/public/Projects";
import ProceduresList from "./pages/admin/procedures/ProceduresList";
import ProcedureForm from "./pages/admin/procedures/ProcedureForm";
import Procedures from "./pages/public/Procedures";
import ProcedureDetail from "./pages/public/ProcedureDetail";
import { getInformation } from "./services/api";

// Helper for color darkening
const getDarkerColor = (hex: string, amount: number = 20) => {
    // Basic darken function for hex
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    
    const nr = Math.max(0, r - amount);
    const ng = Math.max(0, g - amount);
    const nb = Math.max(0, b - amount);
    
    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
}

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top instantly when route changes
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    const applyPrimaryColor = async () => {
      try {
        const response = await getInformation();
        if (response.data && response.data.length > 0) {
          const info = response.data[0];
          if (info.main_color) {
            document.documentElement.style.setProperty('--primary-color', info.main_color);
            document.documentElement.style.setProperty('--primary-dark', getDarkerColor(info.main_color));
            
            // Save to localStorage to avoid flash on refresh
            localStorage.setItem('primaryColor', info.main_color);
            localStorage.setItem('primaryColorDark', getDarkerColor(info.main_color));
          }
        }
      } catch (error) {
        console.error("Setting primary color error:", error);
      }
    };
    applyPrimaryColor();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-grow">
              <ScrollToTop /> {/* Add the ScrollToTop component here */}
              <Routes>
                {/* Public Routes */}
                <Route path="/photos" element={<PhotosPage />} />
                <Route path="/videos" element={<VideosPage />} />
                <Route path="/" element={<Home />} />
                <Route path="/ministry" element={<Ministry />} />
                <Route path="/photos" element={<PhotosPage />} />
                <Route path="/videos" element={<VideosPage />} />
                <Route path="/news" element={<News />} />
                <Route path="/news/:id" element={<NewsDetail />} />
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/procedures" element={<Procedures />} />
                <Route path="/procedures/:id" element={<ProcedureDetail />} />
                <Route
                  path="/announcements/:id"
                  element={<AnnouncementDetail />}
                />
                <Route path="/legislation" element={<Legislation />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/minister" element={<MinisterPage />} />
                <Route path="/minister/:id" element={<MinisterPage />} />

                {/* Admin Routes */}
                <Route path="/admin/login" element={<Login />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/admin/dashboard" element={<Dashboard />} />
                  <Route
                    path="/admin/information"
                    element={<InformationAdmin />}
                  />
                  <Route path="/admin/messages" element={<MessagesAdmin />} />
                  <Route
                    path="/admin/news/categories"
                    element={<NewsCategories />}
                  />
                  <Route
                    path="/admin/news/categories/new"
                    element={<CategorieForm />}
                  />
                  <Route
                    path="/admin/news/categories/edit/:id"
                    element={<CategorieForm />}
                  />
                  <Route path="/admin/projets" element={<ProjetsList />} />
                  <Route path="/admin/projets/new" element={<ProjetForm />} />
                  <Route
                    path="/admin/projets/edit/:id"
                    element={<ProjetForm />}
                  />
                  <Route
                    path="/admin/cabinetministre"
                    element={<CabinetMinistreList />}
                  />
                  <Route
                    path="/admin/cabinetministre/new"
                    element={<CabinetMinistreForm />}
                  />
                  {/* Add Mot Ministre routes */}
                  <Route
                    path="/admin/mot-ministre"
                    element={<MotMinistreList />}
                  />
                  <Route
                    path="/admin/mot-ministre/new"
                    element={<MotMinistreForm />}
                  />
                  <Route
                    path="/admin/mot-ministre/edit/:id"
                    element={<MotMinistreForm />}
                  />
                  <Route
                    path="/admin/structure-admin"
                    element={<StructureAdminList />}
                  />
                  <Route
                    path="/admin/structure-admin/new"
                    element={<StructureAdminForm />}
                  />
                  <Route
                    path="/admin/structure-admin/edit/:id"
                    element={<StructureAdminForm />}
                  />
                  <Route
                    path="/admin/cabinetministre/edit/:id"
                    element={<CabinetMinistreForm />}
                  />
                  <Route
                    path="/admin/sousetablissements"
                    element={<SousEtablissementsList />}
                  />
                  <Route
                    path="/admin/sousetablissements/new"
                    element={<SousEtablissementForm />}
                  />
                  <Route
                    path="/admin/sousetablissements/edit/:id"
                    element={<SousEtablissementForm />}
                  />
                  <Route path="/admin/news" element={<NewsAdmin />} />
                  <Route path="/admin/news/new" element={<NewsForm />} />
                  <Route path="/admin/news/edit/:id" element={<NewsForm />} />
                  <Route
                    path="/admin/bibliotheque/categories"
                    element={<BibliothequeCategories />}
                  />
                  <Route
                    path="/admin/bibliotheque/categories/new"
                    element={<BibliothequeCategorieForm />}
                  />
                  <Route
                    path="/admin/bibliotheque/categories/edit/:id"
                    element={<BibliothequeCategorieForm />}
                  />
                  <Route
                    path="/admin/bibliotheque"
                    element={<BibliothequeList />}
                  />
                  <Route
                    path="/admin/bibliotheque/new"
                    element={<BibliothequeForm />}
                  />
                  <Route
                    path="/admin/bibliotheque/edit/:id"
                    element={<BibliothequeForm />}
                  />
                  <Route path="/admin/sliders" element={<SliderList />} />
                  <Route path="/admin/sliders/new" element={<SliderForm />} />
                  <Route
                    path="/admin/sliders/edit/:id"
                    element={<SliderForm />}
                  />
                  <Route path="/admin/ministers" element={<Ministers />} />
                  <Route
                    path="/admin/ministers/new"
                    element={<MinisterForm />}
                  />
                  <Route
                    path="/admin/ministers/edit/:id"
                    element={<MinisterForm />}
                  />

                  <Route
                    path="/admin/procedures"
                    element={<ProceduresList />}
                  />
                  <Route
                    path="/admin/procedures/new"
                    element={<ProcedureForm />}
                  />
                  <Route
                    path="/admin/procedures/edit/:id"
                    element={<ProcedureForm />}
                  />
                  <Route
                    path="/admin/announcements"
                    element={<AnnouncementsAdmin />}
                  />
                  <Route
                    path="/admin/announcements/new"
                    element={<AnnouncementForm />}
                  />
                  <Route
                    path="/admin/announcements/edit/:id"
                    element={<AnnouncementForm />}
                  />
                  <Route
                    path="/admin/legislations"
                    element={<LegislationsAdmin />}
                  />
                  <Route
                    path="/admin/legislations/new"
                    element={<LegislationForm />}
                  />
                  <Route
                    path="/admin/legislations/edit/:id"
                    element={<LegislationForm />}
                  />
                  <Route path="/admin/users" element={<Users />} />
                  <Route path="/admin/users/new" element={<UserForm />} />
                  <Route path="/admin/users/edit/:id" element={<UserForm />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={true}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
